import { useLoadingStore } from '../../store/loadingStore';
import { useGlobalErrStore } from '../../store/errorStore';
import { convertStringToErrorCode } from '../../api/errorCode';

type ApiFunction<T> = (...args: any[]) => Promise<T>;

interface RetryOptions {
  maxRetries?: number;
  retryableErrorCodes?: string[];
  backoffMs?: number;
}

export const useApiWrapper = () => {
  const addLoading = useLoadingStore((state) => state.addLoading);
  const popLoading = useLoadingStore((state) => state.popLoading);
  const setManagedErr = useGlobalErrStore((state) => state.setManagedErr);
  const clearErr = useGlobalErrStore((state) => state.clearErr);

  return async function withApiHandler<T>(
    apiFunction: ApiFunction<T>,
    params: any[],
    options: RetryOptions = {},
    isLoading: boolean = true
  ): Promise<T | undefined> {
    const {
      maxRetries = 3,
      retryableErrorCodes = ['NETWORK', 'TIMEOUT', 'SERVER'],
      backoffMs = 300
    } = options;

    let currentAttempt = 0;

    const executeWithRetry = async (): Promise<T | undefined> => {
      currentAttempt++;
      //아니 isLoading이 false 일 경우 추가해야 하는거 아냐? 그래서 !isLoading 일 경우 addLoading 하는 걸로 바꿈
      if (isLoading) {
        addLoading();
      }
      clearErr();

      try {
        return await apiFunction(...params);
      } catch (err) {
        const rawErrorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
        let errorCode = 'WEF';
        let errorMessage = rawErrorMessage;

        if (rawErrorMessage.includes('::')) {
          [errorCode, errorMessage] = rawErrorMessage.split('::').map(str => str.trim());
        }

        const convertedErrCode = convertStringToErrorCode(errorCode);
        const canRetry = currentAttempt < maxRetries && retryableErrorCodes.includes(convertedErrCode);
        setManagedErr({
          errCode: convertedErrCode,
          value: errorMessage,
          retryFunction: canRetry ? async () => {
            const delay = backoffMs * Math.pow(2, currentAttempt - 1);
            await new Promise(resolve => setTimeout(resolve, delay));
            await executeWithRetry();
            return;
          } : undefined
        });

        return undefined;
      } finally {
        if (isLoading) {
          popLoading();
        }
      }
    };

    return executeWithRetry();
  };
};